import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import SchoolCard, { CustomListInfo } from '../components/SchoolCard';
import SchoolCardSkeleton from '../components/SchoolCardSkeleton';
import SchoolProfileModal from '../components/SchoolProfileModal';
import CoachReportModal from '../components/CoachReportModal';
import FilterSidebar, { FilterState } from '../components/FilterSidebar';
import CoachNotesSection from '../components/CoachNotesSection';
import { Award, MapPin, Search, Filter, Users, TrendingUp, X, Heart, Flag } from 'react-feather';
import { useAuth } from '../contexts/AuthContext';
import { useSearch } from '../contexts/SearchContext';
import { useToast } from '../contexts/ToastContext';
import { useResponsive } from '../hooks/useResponsive';
import { schoolsAPI, MatchedSchool } from '../services/schoolsAPI';
import { schoolListsAPI, SchoolCategory } from '../services/schoolListsAPI';
import { customListsAPI, CustomList } from '../services/customListsAPI';
import { familyAPI, gamePlanAPI, GamePlan, GamePlanStep } from '../services/api';
import CoachEngagementCard from '../components/CoachEngagementCard';
import ProfileCompletenessPill from '../components/ProfileCompletenessPill';
import ErrorState from '../components/ErrorState';
import { EmptyState } from '../components/ui';
import { AthleteSnapshot } from '../hooks/useProfileCompleteness';
import { swrGet, swrSet, swrInvalidate } from '../utils/swrCache';
import { appScroller, containedScroll, listTopScrollTarget, shouldRefreshList, useScrollportHeight } from '../utils/stickyScroll';
import GamePlanCard, { GamePlanAction, GAME_PLAN_CARD_ID } from '../components/gameplan/GamePlanCard';
import WelcomeVideoModal, { WelcomeCloseInfo } from '../components/gameplan/WelcomeVideoModal';
import AddVideoModal from '../components/gameplan/AddVideoModal';
import EmailProgramsModal from '../components/gameplan/EmailProgramsModal';
import { cleanFirstName, orderedGaps, PROFILE_GAP_SECTION } from '../components/gameplan/gamePlanCopy';
import type { TabType as SchoolTab } from '../components/SchoolProfileModal';

// APS-259 step 4. The deep-link params Dashboard acts on once, then strips.
const GAME_PLAN_PARAMS = ['gp', 'athlete', 'welcome', 'school', 'tab', 'utm_source', 'utm_medium', 'utm_campaign'];
const GP_ACTIONS: GamePlanAction[] = ['schools', 'profile', 'video', 'gmail', 'contact_coach', 'upgrade'];
const GAME_PLAN_TTL_MS = 2 * 60 * 1000;

const Dashboard: React.FC = () => {
  const { sessionToken, user, setUser } = useAuth();
  const { searchQuery, searchNonce } = useSearch();
  const toast = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const { isMobile, isTablet } = useResponsive();
  // Persist the active quick-filter view + filters for the session so leaving
  // /dashboard (into a school, Messages, etc.) and coming back restores what the
  // user was looking at, instead of snapping back to "Matched for you" with
  // filters cleared. sessionStorage is per-tab and clears on tab close.
  const [activeView, setActiveView] = useState<'matched' | 'open' | 'all'>(
    () => {
      const saved = sessionStorage.getItem('dashboardActiveView');
      return saved === 'matched' || saved === 'open' || saved === 'all' ? saved : 'matched';
    }
  );
  const [schools, setSchools] = useState<MatchedSchool[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  // Real error vs. genuinely-empty results (BUG_LIST #172): a failed fetch must
  // NOT masquerade as "No schools found".
  const [loadError, setLoadError] = useState(false);
  // Monotonic request id so an out-of-order response can't clobber a newer one
  // (#169) and the scroll sentinel can't fire a duplicate page-1 mid-debounce
  // (#170). loadMoreFailed backs off the observer after a failed page (#176).
  const requestIdRef = useRef(0);
  const page1PendingRef = useRef(false);
  // True while a view, filter or search change reloads the list.
  const [listReloading, setListReloading] = useState(false);
  // Loaded card count as of the last render, read by async loaders.
  const schoolsCountRef = useRef(0);
  schoolsCountRef.current = schools.length;
  const [loadMoreFailed, setLoadMoreFailed] = useState(false);
  const [athleteId, setAthleteId] = useState<string>('');
  const [athleteSnapshot, setAthleteSnapshot] = useState<AthleteSnapshot | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [columnsCount, setColumnsCount] = useState(3);
  const [itemsPerPage, setItemsPerPage] = useState(21); // Default 3 columns = 21 items
  const [filters, setFilters] = useState<FilterState>(() => {
    try { return JSON.parse(sessionStorage.getItem('dashboardFilters') || '{}'); }
    catch { return {}; }
  });
  const [selectedSchoolId, setSelectedSchoolId] = useState<number | null>(null);
  const [isSchoolModalOpen, setIsSchoolModalOpen] = useState(false);
  const [schoolModalTab, setSchoolModalTab] = useState<SchoolTab | undefined>(undefined);
  // APS-260: the school whose coach change report is open.
  const [reportSchoolId, setReportSchoolId] = useState<number | null>(null);

  // APS-259 step 4: the recruiting game plan. null = off, hidden, or not loaded.
  const [gamePlan, setGamePlan] = useState<GamePlan | null>(null);
  const [gamePlanLoaded, setGamePlanLoaded] = useState(false);
  const [athleteCount, setAthleteCount] = useState(1);
  const [myAthletes, setMyAthletes] = useState<Array<AthleteSnapshot & { id: string }> | null>(null);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [addVideoOpen, setAddVideoOpen] = useState(false);
  const [emailProgramsOpen, setEmailProgramsOpen] = useState(false);
  const [highlightStep, setHighlightStep] = useState<GamePlanStep['key'] | null>(null);
  const welcomeAutoShownRef = useRef(false);
  const deepLinkHandledRef = useRef('');
  const deepLinkSwitchRef = useRef('');
  const gamePlanReqRef = useRef(0);
  const schoolsAnchorRef = useRef<HTMLDivElement | null>(null);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false); // Mobile filter modal
  const scrollportHeight = useScrollportHeight();
  
  // School list interactions (heart/star/bookmark pills)
  const [schoolInteractions, setSchoolInteractions] = useState<{ [key: number]: { heart: boolean; star: boolean; bookmark: boolean } }>({});
  const [pillLoadingStates, setPillLoadingStates] = useState<{ [key: string]: boolean | string }>({});

  // Custom lists state
  const [customLists, setCustomLists] = useState<CustomList[]>([]);
  const [schoolMemberships, setSchoolMemberships] = useState<{ [schoolId: number]: number[] }>({});

  // Detect columns and adjust items per page
  useEffect(() => {
    const detectColumns = () => {
      const grid = document.querySelector('[data-school-grid]');
      if (grid) {
        const gridWidth = grid.clientWidth;
        const cardMinWidth = 340;
        const gap = 20;
        const columns = Math.floor((gridWidth + gap) / (cardMinWidth + gap));
        setColumnsCount(columns);
        
        // If 3 columns: divisible by 3 = 21
        // Otherwise: 20 (divisible by 1,2,4)
        setItemsPerPage(columns === 3 ? 21 : 20);
      }
    };

    detectColumns();
    window.addEventListener('resize', detectColumns);
    return () => window.removeEventListener('resize', detectColumns);
  }, []);

  // (Removed a duplicate column-detection effect — the effect above already
  // derives columnsCount + the load-bearing itemsPerPage from the same grid.)

  // Persist view + filters so they survive leaving and returning to /dashboard.
  useEffect(() => {
    sessionStorage.setItem('dashboardActiveView', activeView);
  }, [activeView]);
  useEffect(() => {
    sessionStorage.setItem('dashboardFilters', JSON.stringify(filters));
  }, [filters]);

  // Live ref to the latest loadAthleteAndSchools, updated every render, so the
  // focus/visibility listener (bound once) never calls a stale closure.
  const loadAthleteAndSchoolsRef = useRef<() => void>(() => {});

  // Load athlete on mount
  useEffect(() => {
    loadAthleteAndSchools();
  }, [sessionToken]);

  // Live refresh on window focus / tab visibility change. Without this, the
  // dashboard's athleteSnapshot is stale after the user edits their profile
  // in another tab or navigates to /profile and fills in stats — the gap
  // banner kept nagging even after the gap was closed (BUG A in the 5/13
  // walkthrough). Cheap enough: same single GET /families/me/my-athletes.
  useEffect(() => {
    if (!sessionToken) return;
    // Call through a ref so the once-bound listener always runs the CURRENT
    // view + search. Previously it captured the initial-mount closure
    // (activeView='matched', empty search), so refocusing while on "All
    // Schools" with a search term reverted the list to "Matched for you".
    // Throttle: focus + visibilitychange both fire on an alt-tab, and each fired
    // a fetch — 2 requests per alt-tab, unthrottled (#149). Cap to once / 30s.
    let lastRefresh = 0;
    const refresh = () => {
      if (document.hidden) return;
      const now = Date.now();
      if (now - lastRefresh < 30000) return;
      lastRefresh = now;
      loadAthleteAndSchoolsRef.current();
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionToken]);

  // Load school lists and custom lists when athlete ID is available
  useEffect(() => {
    if (athleteId && sessionToken) {
      loadSchoolLists();
      loadCustomLists();
      loadGamePlan(athleteId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [athleteId, sessionToken]);

  // Check URL params for search navigation from other pages
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('view') === 'all' && params.get('search') === 'true') {
      setActiveView('all');
      setFilters({});
      navigate('/dashboard', { replace: true });
    }
  }, [location.search]);

  // Reload schools when activeView, filters, or search changes
  useEffect(() => {
    if (athleteId) {
      // Scrolled past the list's start? Go back to it, not to the page top.
      const scroller = appScroller();
      const anchor = schoolsAnchorRef.current;
      if (scroller && anchor) {
        const target = listTopScrollTarget(
          scroller.scrollTop,
          anchor.getBoundingClientRect().top - scroller.getBoundingClientRect().top
        );
        if (target !== null) scroller.scrollTo({ top: target, behavior: 'smooth' });
      }
      setListReloading(true);
      setSchools([]);
      setHasMore(true);
      // Gate the infinite-scroll sentinel until page 1 actually resolves, so it
      // can't fire a duplicate page-1 fetch during the 300ms search debounce (#170).
      page1PendingRef.current = true;
      setLoadMoreFailed(false);

      // Search and filters coexist on "All Schools": the 'all' loadSchools path
      // already sends filterParams AND query to filterSchools, so there's no need
      // to force-clear one when the other is set (#198).

      // Debounce if search is active
      if (searchQuery.trim()) {
        const timer = setTimeout(() => {
          loadSchools();
        }, 300);
        return () => clearTimeout(timer);
      } else {
        loadSchools();
      }
    }
    // searchNonce is in the deps so an explicit re-submit of the same term
    // still re-runs the search (searchQuery alone wouldn't change).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView, athleteId, filters, searchQuery, searchNonce]);

  // Infinite scroll: use IntersectionObserver on a sentinel element for reliability
  const loaderRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!loaderRef.current) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // When sentinel is visible, attempt to load more. Never while page 1
          // is still pending (#170) or after a failed load-more (#176).
          if (!loading && !loadingMore && hasMore && !page1PendingRef.current && !loadMoreFailed) {
            loadMoreSchools();
          }
        }
      });
    }, {
      root: null,
      rootMargin: '500px', // start loading when sentinel is within 500px of viewport
      threshold: 0
    });

    observer.observe(loaderRef.current);

    return () => observer.disconnect();
  }, [loading, loadingMore, hasMore, loadMoreFailed, loaderRef.current]);

  const loadAthleteAndSchools = async () => {
    if (!sessionToken) return;
    // Whether an athlete was already resolved when this call started
    // distinguishes a focus-refresh (true) from the initial mount (false).
    const isRefresh = !!athleteId;

    try {
      // Stale-while-revalidate on the athlete resolution: a cached snapshot
      // resolves athleteId immediately on mount (schools start loading without
      // waiting a network round trip); the fetch below still runs and updates
      // the snapshot, so gap-banner data always converges to fresh.
      if (!isRefresh) {
        const cachedAthlete = swrGet<AthleteSnapshot & { id: string }>('myAthletes:first', 10 * 60 * 1000);
        if (cachedAthlete?.id) {
          setAthleteId(cachedAthlete.id); // reload effect fetches schools
          setAthleteSnapshot(cachedAthlete);
        } else {
          setLoading(true);
        }
      }

      // Single source of truth for "which athlete drives this dashboard":
      // GET /families/me/my-athletes. Resolves to the athlete_profile for
      // athlete users, or the linked athlete(s) via guardian_athlete_relationships
      // for guardians. Pre-freemium this page guessed via familyMember
      // .athlete_profile_id, which is null for guardians and rendered empty.
      const myAthletes = await familyAPI.getMyAthletes(sessionToken);
      const first = myAthletes.athletes?.[0];
      setAthleteCount(myAthletes.athletes?.length || 1);
      setMyAthletes(myAthletes.athletes || []);
      if (first?.id) {
        swrSet('myAthletes:first', first);
        setAthleteSnapshot(first); // P2.5 — drives the gap banner
        setAthleteId(first.id);
        // Fetch schools here ONLY on a focus-refresh: athleteId is unchanged
        // then, so the reload effect won't fire. On mount, setAthleteId
        // ('' -> id, from cache above or just now) fires the reload effect —
        // also calling loadSchools here made the dashboard fetch its list
        // TWICE on every mount.
        if (isRefresh && first.id === athleteId) {
          loadGamePlan(first.id);
          // Refresh in place. Skip a list with more than one page loaded.
          if (shouldRefreshList(schools.length, itemsPerPage)) {
            await loadSchools(first.id, { background: true });
          }
        }
      }
    } catch (error) {
      console.error('Error loading athlete:', error);
    } finally {
      setLoading(false);
    }
  };

  // Keep the focus/visibility listener pointed at the current-closure loader.
  useEffect(() => {
    loadAthleteAndSchoolsRef.current = loadAthleteAndSchools;
  });

  // ---- APS-259 step 4: game plan ----------------------------------------
  // The plan carries per-viewer fields, so the cache is per login too.
  const gamePlanKey = (id: string) => `gamePlan:${user?.id || ''}:${id}`;

  const loadGamePlan = async (id?: string) => {
    const target = id || athleteId;
    if (!sessionToken || !target) return;
    const reqId = ++gamePlanReqRef.current;
    const key = gamePlanKey(target);
    const cached = swrGet<GamePlan | null>(key, GAME_PLAN_TTL_MS);
    if (cached && cached.athlete?.id === target) {
      setGamePlan(cached);
      setGamePlanLoaded(true);
    } else if (gamePlan && gamePlan.athlete.id !== target) {
      // The guardian switched athletes: never show the last one's plan.
      setGamePlan(null);
    }
    try {
      const res = await gamePlanAPI.get(sessionToken, target);
      if (reqId !== gamePlanReqRef.current) return;
      const plan = res.enabled ? res : null;
      setGamePlan(plan);
      swrSet(key, plan);
    } catch (error) {
      // Off when the API can't answer: the dashboard reads as it did before.
      if (reqId !== gamePlanReqRef.current) return;
      console.warn('Game plan unavailable:', error);
      if (!cached) setGamePlan(null);
    } finally {
      if (reqId === gamePlanReqRef.current) setGamePlanLoaded(true);
    }
  };

  const showGamePlanCard = !!gamePlan && gamePlan.checklist.show && !gamePlan.checklist.hidden;

  const scrollToGamePlan = (highlight: boolean) => {
    window.setTimeout(() => {
      const el = document.getElementById(GAME_PLAN_CARD_ID);
      if (!el) return;
      try { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch { el.scrollIntoView(); }
      const firstOpen = gamePlan?.steps.find((s) => !s.done)?.key;
      if (highlight && firstOpen) {
        setHighlightStep(firstOpen);
        window.setTimeout(() => setHighlightStep(null), 2000);
      }
    }, 50);
  };

  const openSchool = (schoolId: number, tab?: SchoolTab) => {
    setSchoolModalTab(tab);
    setSelectedSchoolId(schoolId);
    setIsSchoolModalOpen(true);
  };

  const showMatches = () => {
    setActiveView('matched');
    window.setTimeout(() => {
      try { schoolsAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch {}
    }, 50);
  };

  const runGamePlanAction = (action: GamePlanAction, step?: GamePlanStep) => {
    const plan = gamePlan;
    const theStep = step || plan?.steps.find((s) => s.key === action);
    switch (action) {
      case 'schools':
        showMatches();
        break;
      case 'profile': {
        const gap = theStep ? orderedGaps(theStep)[0] : undefined;
        navigate(gap ? `/profile?openModal=${PROFILE_GAP_SECTION[gap]}` : '/profile');
        break;
      }
      case 'video':
        if (athleteId) setAddVideoOpen(true);
        break;
      case 'gmail':
        navigate('/settings/connections');
        break;
      case 'contact_coach':
        // The bulk program email, with the saved schools picked.
        if (athleteId) setEmailProgramsOpen(true);
        break;
      case 'upgrade':
        navigate('/subscription-required');
        break;
    }
  };

  const markWelcomeSeen = () => {
    if (!gamePlan?.welcome.show || !sessionToken) return;
    const next: GamePlan = { ...gamePlan, welcome: { ...gamePlan.welcome, show: false } };
    setGamePlan(next);
    swrSet(gamePlanKey(next.athlete.id), next);
    gamePlanAPI.welcomeSeen(sessionToken).catch((e) => console.warn('welcome-seen failed:', e));
  };

  const handleWelcomeClose = (_info: WelcomeCloseInfo) => {
    setWelcomeOpen(false);
    markWelcomeSeen();
    scrollToGamePlan(true);
  };

  const handleHideGamePlan = async (): Promise<boolean> => {
    if (!sessionToken || !athleteId || !gamePlan) return false;
    try {
      await gamePlanAPI.hide(sessionToken, athleteId);
      const next: GamePlan = { ...gamePlan, checklist: { ...gamePlan.checklist, hidden: true } };
      setGamePlan(next);
      swrSet(gamePlanKey(athleteId), next);
      return true;
    } catch (error: any) {
      if (error?.code === 'GAME_PLAN_NOT_DONE') {
        toast.error('Finish every step first.');
        loadGamePlan();
      } else {
        toast.error('Could not hide the game plan. Try again.');
      }
      return false;
    }
  };

  // First visit after sign-up: the welcome video opens by itself, once.
  // Only with the checklist on screen, since the video leads into it.
  useEffect(() => {
    if (!gamePlan?.welcome.show || !showGamePlanCard || welcomeAutoShownRef.current) return;
    welcomeAutoShownRef.current = true;
    setWelcomeOpen(true);
  }, [gamePlan?.welcome.show, showGamePlanCard]);

  // A guardian lands on the athlete the link names, the same switch
  // PlayerProfile makes.
  const switchToLinkedAthlete = async (linkedId: string) => {
    if (!sessionToken || !user?.id) throw new Error('Not signed in');
    await familyAPI.updateMemberProfileIds(sessionToken, user.id, { athlete_profile_id: linkedId });
    setUser({ ...user, athlete_profile_id: linkedId });
    const snap = myAthletes?.find((a) => a.id === linkedId);
    if (snap) {
      swrSet('myAthletes:first', snap);
      setAthleteSnapshot(snap);
    }
    setGamePlanLoaded(false);
    setAthleteId(linkedId);
  };

  // Deep links from game plan emails and pushes: ?gp=, ?athlete=, ?welcome=1,
  // ?school=<id>&tab=coaching. Act once, then drop the params.
  // App.tsx tracks the email click (GamePlanEmailClickTracker).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!GAME_PLAN_PARAMS.some((k) => params.has(k))) return;
    if (!athleteId || !gamePlanLoaded) return;
    if (deepLinkHandledRef.current === location.search) return;

    const dropParams = () => {
      deepLinkHandledRef.current = location.search;
      const rest = new URLSearchParams(location.search);
      GAME_PLAN_PARAMS.forEach((k) => rest.delete(k));
      const qs = rest.toString();
      navigate(`/dashboard${qs ? `?${qs}` : ''}`, { replace: true });
    };

    const linked = params.get('athlete');
    if (linked && linked !== athleteId) {
      if (!myAthletes) return; // wait for the family's athletes
      if (deepLinkSwitchRef.current === location.search) return;
      deepLinkSwitchRef.current = location.search;
      if (!myAthletes.some((a) => a.id === linked)) {
        dropParams();
        return;
      }
      switchToLinkedAthlete(linked).catch((error) => {
        console.warn('Could not switch to the linked athlete:', error);
        dropParams();
      });
      return;
    }
    // After a switch, wait for that athlete's plan.
    if (gamePlan && gamePlan.athlete.id !== athleteId) return;

    const gp = params.get('gp');
    const welcome = params.get('welcome');
    const school = params.get('school');
    const tab = params.get('tab') as SchoolTab | null;

    dropParams();

    // With the feature off the API says enabled: false and these do nothing.
    if (gamePlan) {
      if (welcome === '1' || gp === 'welcome') {
        // The welcome leads into the checklist, so it needs the card.
        if (showGamePlanCard) {
          if (welcome === '1' || gamePlan.welcome.show) setWelcomeOpen(true);
          else scrollToGamePlan(false);
        }
      } else if (gp === 'upgrade') {
        if (gamePlan.upgrade.show) runGamePlanAction('upgrade');
        else if (showGamePlanCard) scrollToGamePlan(false);
      } else if (gp && (GP_ACTIONS as string[]).includes(gp)) {
        runGamePlanAction(gp as GamePlanAction);
      }
    }
    if (school && /^\d+$/.test(school)) {
      const validTabs: SchoolTab[] = ['overview', 'academics', 'coaching', 'athletics', 'roster'];
      openSchool(Number(school), tab && validTabs.includes(tab) ? tab : undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, athleteId, gamePlanLoaded, myAthletes, gamePlan?.athlete.id]);

  const loadSchools = async (id?: string, opts?: { background?: boolean }) => {
    const background = !!opts?.background;
    const targetAthleteId = id || athleteId;
    if (!sessionToken || !targetAthleteId) {
      setListReloading(false);
      return;
    }

    // Monotonic request id: a slow earlier response must not overwrite a newer
    // one (#169). Only the latest request is allowed to commit its results.
    const reqId = ++requestIdRef.current;

    // Stale-while-revalidate keyed on everything that changes the result set:
    // returning to a view/search/filter combo paints the last list instantly;
    // the fetch below still runs and replaces it with fresh data.
    const listKey = `dashSchools:${targetAthleteId}:${activeView}:${searchQuery.trim()}:${itemsPerPage}:${JSON.stringify(filters)}`;
    const cachedList = swrGet<{ schools: MatchedSchool[]; hasMore: boolean }>(listKey, 10 * 60 * 1000);

    if (!background) setLoadError(false);
    try {
      if (background) {
        // Keep the list on screen until the new page arrives.
      } else if (cachedList) {
        setSchools(cachedList.schools);
        setHasMore(cachedList.hasMore);
        setLoading(false);
      } else {
        setLoading(true);
      }

      const hasCustomFilters = Object.keys(filters).length > 0;
      let response: any;
      
      // Quick Filters: Matched for You or Open Rosters
      // Search within these results if searchQuery exists
      if (activeView === 'matched') {
        response = await schoolsAPI.getMatchedForYou(sessionToken, targetAthleteId, {
          limit: itemsPerPage,
          offset: 0,
          query: searchQuery.trim() || undefined
        });
      } else if (activeView === 'open') {
        response = await schoolsAPI.getOpenRosters(sessionToken, targetAthleteId, {
          limit: itemsPerPage,
          offset: 0,
          query: searchQuery.trim() || undefined
        });
      }
      // All Schools: Either with custom filters OR just all schools
      else {
        const filterParams = hasCustomFilters ? {
          ...(filters.division && { division: filters.division }),
          ...(filters.conference && { conference: filters.conference }),
          ...(filters.state && { state: filters.state }),
          ...(filters.rosterStatus && { rosterStatus: filters.rosterStatus }),
          ...(filters.majors && { majors: filters.majors }),
          ...(filters.acceptanceRate?.min !== null && filters.acceptanceRate?.min !== undefined && { 
            acceptanceRateMin: filters.acceptanceRate.min 
          }),
          ...(filters.acceptanceRate?.max !== null && filters.acceptanceRate?.max !== undefined && { 
            acceptanceRateMax: filters.acceptanceRate.max 
          }),
          ...(filters.costOfAttendance?.min !== null && filters.costOfAttendance?.min !== undefined && { 
            costMin: filters.costOfAttendance.min 
          }),
          ...(filters.costOfAttendance?.max !== null && filters.costOfAttendance?.max !== undefined && { 
            costMax: filters.costOfAttendance.max 
          }),
          ...(filters.totalStudentCount?.min !== null && filters.totalStudentCount?.min !== undefined && { 
            studentCountMin: filters.totalStudentCount.min 
          }),
          ...(filters.totalStudentCount?.max !== null && filters.totalStudentCount?.max !== undefined && { 
            studentCountMax: filters.totalStudentCount.max 
          })
        } : {};
        
        response = await schoolsAPI.filterSchools(sessionToken, targetAthleteId, {
          ...filterParams,
          limit: itemsPerPage,
          offset: 0,
          query: searchQuery.trim() || undefined
        });
      }
      
      // Discard if a newer request superseded this one (#169).
      if (reqId !== requestIdRef.current) return;

      const newSchools = response.matched_schools || [];
      if (background) {
        // A load-more landed while this ran. Keep the longer list.
        if (schoolsCountRef.current > itemsPerPage) return;
        setSchools(prev => (prev.length > itemsPerPage ? prev : newSchools));
      } else {
        setSchools(newSchools);
      }

      // Check if there are more schools using pagination info
      const nextHasMore = response.pagination?.has_next || false;
      setHasMore(nextHasMore);
      swrSet(listKey, { schools: newSchools, hasMore: nextHasMore });

    } catch (error) {
      if (reqId !== requestIdRef.current) return;
      console.error('Error loading schools:', error);
      // A failed fetch is an ERROR, not an empty result (#172). Surface a retry
      // instead of the misleading "No schools found". Keep any cached list up.
      // A failed background refresh keeps the current list.
      if (!cachedList && !background) {
        setSchools([]);
        setLoadError(true);
      }
    } finally {
      if (reqId === requestIdRef.current) {
        setLoading(false);
        page1PendingRef.current = false;
        requestAnimationFrame(() => setListReloading(false));
      }
    }
  };

  const loadMoreSchools = async () => {
    if (!sessionToken || !athleteId || loadingMore || !hasMore) return;

    try {
      setLoadingMore(true);
      setLoadMoreFailed(false);

      // Page 2+ MUST hit the same endpoint page 1 did, which is chosen by
      // activeView, not by whether filters happen to be non-empty (#168). Only
      // the 'all' view applies filters — 'matched'/'open' ignore them exactly
      // like loadSchools, so scrolling can no longer append unrelated results.
      const options = {
        limit: itemsPerPage,
        offset: schools.length,
        ...(searchQuery.trim() && { query: searchQuery.trim() }),
      };
      let response;

      if (activeView === 'matched') {
        response = await schoolsAPI.getMatchedForYou(sessionToken, athleteId, options);
      } else if (activeView === 'open') {
        response = await schoolsAPI.getOpenRosters(sessionToken, athleteId, options);
      } else {
        // 'all' view — apply the active filters (same param mapping as page 1).
        const hasCustomFilters = Object.keys(filters).length > 0;
        const filterParams = hasCustomFilters ? {
          ...(filters.division && { division: filters.division }),
          ...(filters.conference && { conference: filters.conference }),
          ...(filters.state && { state: filters.state }),
          ...(filters.rosterStatus && { rosterStatus: filters.rosterStatus }),
          ...(filters.majors && { majors: filters.majors }),
          ...(filters.acceptanceRate?.min !== null && filters.acceptanceRate?.min !== undefined && {
            acceptanceRateMin: filters.acceptanceRate.min
          }),
          ...(filters.acceptanceRate?.max !== null && filters.acceptanceRate?.max !== undefined && {
            acceptanceRateMax: filters.acceptanceRate.max
          }),
          ...(filters.costOfAttendance?.min !== null && filters.costOfAttendance?.min !== undefined && {
            costMin: filters.costOfAttendance.min
          }),
          ...(filters.costOfAttendance?.max !== null && filters.costOfAttendance?.max !== undefined && {
            costMax: filters.costOfAttendance.max
          }),
          ...(filters.totalStudentCount?.min !== null && filters.totalStudentCount?.min !== undefined && {
            studentCountMin: filters.totalStudentCount.min
          }),
          ...(filters.totalStudentCount?.max !== null && filters.totalStudentCount?.max !== undefined && {
            studentCountMax: filters.totalStudentCount.max
          }),
        } : {};

        response = await schoolsAPI.filterSchools(sessionToken, athleteId, { ...filterParams, ...options });
      }

      const newSchools = response.matched_schools || [];

      if (newSchools.length > 0) {
        setSchools(prev => [...prev, ...newSchools]);
      }

      // Check if there are more schools using pagination info
      setHasMore(response.pagination?.has_next || false);

    } catch (error) {
      console.error('Error loading more schools:', error);
      // Stop the observer from re-firing this failed page forever (battery/data
      // drain, #176). Cleared on the next view/filter/search change.
      setLoadMoreFailed(true);
    } finally {
      setLoadingMore(false);
    }
  };

  const loadSchoolLists = async () => {
    if (!sessionToken || !athleteId) return;
    
    try {
      const response = await schoolListsAPI.getSchoolLists(sessionToken, athleteId);
      
      if (response.success) {
        // Convert to interaction state format
        const interactions: { [key: number]: { heart: boolean; star: boolean; bookmark: boolean } } = {};
        
        // Process all schools from all categories
        const allSchools = [
          ...response.data.dreamSchools.map(s => ({ ...s, category: 'dream' as SchoolCategory })),
          ...response.data.targetSchools.map(s => ({ ...s, category: 'target' as SchoolCategory })),
          ...response.data.safetySchools.map(s => ({ ...s, category: 'safety' as SchoolCategory }))
        ];
        
        allSchools.forEach(school => {
          interactions[school.school_id] = {
            heart: school.category === 'dream',
            star: school.category === 'target',
            bookmark: school.category === 'safety'
          };
        });
        
        setSchoolInteractions(interactions);
      }
    } catch (error) {
      console.error('Error loading school lists:', error);
    }
  };

  // Custom lists loading and handlers
  const loadCustomLists = async () => {
    if (!sessionToken || !athleteId) return;
    try {
      const [listsRes, membershipsRes] = await Promise.all([
        customListsAPI.getCustomLists(sessionToken, athleteId),
        customListsAPI.getSchoolMemberships(sessionToken, athleteId)
      ]);
      if (listsRes.success) setCustomLists(listsRes.data.lists);
      if (membershipsRes.success) setSchoolMemberships(membershipsRes.data.memberships);
    } catch (error) {
      console.error('Error loading custom lists:', error);
    }
  };

  const handleToggleCustomList = async (schoolId: number, listId: number, isInList: boolean) => {
    if (!sessionToken || !athleteId) return;
    try {
      if (isInList) {
        await customListsAPI.removeSchoolFromList(sessionToken, athleteId, listId, schoolId);
      } else {
        await customListsAPI.addSchoolToList(sessionToken, athleteId, listId, schoolId);
      }
      await loadCustomLists();
    } catch (error) {
      console.error('Error toggling custom list:', error);
      toast.error('Could not update the list. Please try again.');
    }
  };

  const handleCreateCustomListFromCard = async (schoolId: number, listName: string, color: string) => {
    if (!sessionToken || !athleteId) return;
    try {
      const createRes = await customListsAPI.createList(sessionToken, athleteId, listName, color);
      if (createRes.success && schoolId) {
        await customListsAPI.addSchoolToList(sessionToken, athleteId, createRes.data.id, schoolId);
      }
      await loadCustomLists();
    } catch (error) {
      console.error('Error creating custom list:', error);
      toast.error('Could not create the list. Please try again.');
    }
  };

  const handlePillPress = async (schoolId: number, pillType: 'dream' | 'target' | 'safety') => {
    if (!sessionToken || !athleteId) return;
    
    const pillKey = `${schoolId}-${pillType}`;
    const pillTypeMap: { [key: string]: 'heart' | 'star' | 'bookmark' } = {
      dream: 'heart',
      target: 'star',
      safety: 'bookmark'
    };
    const actualPillType = pillTypeMap[pillType];
    
    // Set loading state
    setPillLoadingStates(prev => ({ ...prev, [pillKey]: true, [`${schoolId}-targetType`]: actualPillType }));
    
    const currentState = schoolInteractions[schoolId]?.[actualPillType];
    
    try {
      if (currentState) {
        // Remove from list
        await schoolListsAPI.removeSchool(sessionToken, athleteId, schoolId);
      } else {
        // Add to category
        await schoolListsAPI.addSchool(sessionToken, athleteId, schoolId, pillType);
      }
      
      // Update local state (only after the write actually succeeded).
      setSchoolInteractions(current => {
        const newState = { ...current };

        if (currentState) {
          // Removing - turn off all pills
          newState[schoolId] = {
            heart: false,
            star: false,
            bookmark: false
          };
        } else {
          // Adding - turn off all pills, then turn on the selected one
          newState[schoolId] = {
            heart: actualPillType === 'heart',
            star: actualPillType === 'star',
            bookmark: actualPillType === 'bookmark'
          };
        }

        return newState;
      });

      // Counts on the College Lists page are now stale — drop their cache so the
      // next visit paints fresh numbers (#152).
      swrInvalidate((k) => k.startsWith('collegeLists:') || k.startsWith('dashSchools:'));
      // Picking schools is step 1 of the game plan.
      if (gamePlan) loadGamePlan();

    } catch (error) {
      // BUG_LIST #162: the old catch faked success "for demo purposes", so the
      // athlete saw a school hearted that never reached the server and vanished
      // on reload. Do NOT touch local state on failure — surface the error.
      console.error('Error updating school list:', error);
      toast.error('Could not update your list. Please try again.');
    } finally {
      // Clear loading state
      setPillLoadingStates(prev => {
        const newState = { ...prev };
        delete newState[pillKey];
        delete newState[`${schoolId}-targetType`];
        return newState;
      });
    }
  };

  const handleAddToList = (schoolId: number, listType: 'dream' | 'target' | 'safety') => {
    handlePillPress(schoolId, listType);
  };

  const handleSchoolClick = (schoolId: number) => {
    openSchool(schoolId);
  };

  const handleCloseSchoolModal = () => {
    setIsSchoolModalOpen(false);
    setSelectedSchoolId(null);
    setSchoolModalTab(undefined);
    // A coach may have been unlocked or emailed from the modal.
    if (gamePlan) loadGamePlan();
  };

  const handleApplyFilters = (newFilters: FilterState) => {
    // Applying filters switches to "All Schools" view. Search is preserved — the
    // 'all' path sends both query and filters to filterSchools, so they combine.
    setActiveView('all');
    setFilters(newFilters);
    setListReloading(true);
    setSchools([]);
    setHasMore(true);
  };

  const handleClearFilters = () => {
    setFilters({});
    setListReloading(true);
    setSchools([]);
    setHasMore(true);
  };

  return (
    <AppLayout>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      {/* The game plan leads the page, above coach notes. */}
      {showGamePlanCard && gamePlan && athleteId && (
        <div style={{ padding: isMobile ? '16px 16px 0' : '24px 24px 0', maxWidth: '1600px', margin: '0 auto', fontFamily: 'Manrope, sans-serif' }}>
          <GamePlanCard
            plan={gamePlan}
            athleteId={athleteId}
            athleteCount={athleteCount}
            highlightKey={highlightStep}
            onAction={runGamePlanAction}
            onWatchWelcome={() => setWelcomeOpen(true)}
            onHide={handleHideGamePlan}
          />
        </div>
      )}
      {/* Coach Notes Section - visible to athletes and guardians who have notes from staff */}
      {user?.user_type !== 'staff' && sessionToken && (
        <div style={{ padding: isMobile ? '16px 16px 0' : '24px 24px 0', maxWidth: '1600px', margin: '0 auto', fontFamily: 'Manrope, sans-serif' }}>
          <CoachNotesSection />
        </div>
      )}
      <div style={{ 
        display: 'flex', 
        gap: isMobile ? '16px' : '24px', 
        padding: isMobile ? '16px' : '24px', 
        maxWidth: '1600px', 
        margin: '0 auto', 
        fontFamily: 'Manrope, sans-serif',
        flexDirection: isMobile ? 'column' : 'row',
        // No scroll anchoring while the list reloads, so the page does not move.
        overflowAnchor: listReloading ? 'none' : undefined,
      }} data-results-row>
        <div style={{ flex: 1 }}>
          {/* Persistent completeness pill — counts every recommended-tier
              gap and stays visible even after the user dismisses the
              louder banner below. (P2.7+ BUG B from the 5/13 walkthrough.) */}
          {/* The game plan's profile row replaces the completeness pill here.
              The pill stays on every other page. */}
          {athleteSnapshot && gamePlanLoaded && !showGamePlanCard && (
            <ProfileCompletenessPill athlete={athleteSnapshot} />
          )}
          {/* One completeness component across the app — this same pill is used on
              Find Colleges, My Lists, and mobile (the old ProfileGapBanner variant
              was retired for consistency). */}
          {/* Phase 1 monetization: coach-engagement teaser. Renders only when a
              coach has actually engaged; free sees the count + upgrade CTA, paid
              sees identities. Sourced from GET /engagement/coaches. */}
          {athleteSnapshot && <CoachEngagementCard athleteId={athleteSnapshot.id} />}
          {/* Tabs and list. At least one screen tall so a reload keeps the page height. */}
          <div style={{ minHeight: scrollportHeight > 0 ? `${scrollportHeight}px` : undefined }}>
          {/* View Tabs - Horizontally scrollable on mobile */}
          <div ref={schoolsAnchorRef} style={{ 
            scrollMarginTop: 80,
            display: 'flex', 
            gap: isMobile ? '8px' : '12px', 
            marginBottom: isMobile ? '16px' : '24px',
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
            paddingBottom: '4px', // For scrollbar
            msOverflowStyle: 'none', // Hide scrollbar IE
            scrollbarWidth: 'none', // Hide scrollbar Firefox
          }}>
            <button 
              onClick={() => setActiveView('matched')} 
              style={{ 
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: isMobile ? '10px 14px' : '10px 20px', 
                border: 'none', 
                borderRadius: '8px', 
                fontSize: '14px', 
                fontWeight: '500', 
                cursor: 'pointer', 
                transition: 'all 0.2s', 
                background: activeView === 'matched' ? '#F9550D' : 'white', 
                color: activeView === 'matched' ? 'white' : '#64748b', 
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              <Heart size={16} />
              Potential Matches
            </button>
            <button 
              onClick={() => setActiveView('open')} 
              style={{ 
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: isMobile ? '10px 14px' : '10px 20px', 
                border: 'none', 
                borderRadius: '8px', 
                fontSize: '14px', 
                fontWeight: '500', 
                cursor: 'pointer', 
                transition: 'all 0.2s', 
                background: activeView === 'open' ? '#F9550D' : 'white', 
                color: activeView === 'open' ? 'white' : '#64748b', 
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              <Flag size={16} />
              Open Rosters
            </button>
            <button 
              onClick={() => setActiveView('all')} 
              style={{ 
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: isMobile ? '10px 14px' : '10px 20px', 
                border: 'none', 
                borderRadius: '8px', 
                fontSize: '14px', 
                fontWeight: '500', 
                cursor: 'pointer', 
                transition: 'all 0.2s', 
                background: activeView === 'all' ? '#F9550D' : 'white', 
                color: activeView === 'all' ? 'white' : '#64748b', 
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              <Filter size={16} />
              All Schools
            </button>
            
            {/* Mobile Filter Button - Only show on All Schools view */}
            {(isMobile || isTablet) && activeView === 'all' && (
              <button 
                onClick={() => setIsFilterModalOpen(true)} 
                style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  padding: '10px 14px', 
                  border: Object.keys(filters).length > 0 ? '2px solid #F9550D' : '1px solid #e5e7eb', 
                  borderRadius: '8px', 
                  fontSize: '14px', 
                  fontWeight: '500', 
                  cursor: 'pointer', 
                  transition: 'all 0.2s', 
                  background: 'white', 
                  color: Object.keys(filters).length > 0 ? '#F9550D' : '#64748b', 
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  marginLeft: 'auto',
                }}
              >
                <Filter size={16} />
                Filters
                {Object.keys(filters).length > 0 && (
                  <span style={{
                    background: '#F9550D',
                    color: 'white',
                    borderRadius: '50%',
                    width: '18px',
                    height: '18px',
                    fontSize: '11px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    {Object.keys(filters).length}
                  </span>
                )}
              </button>
            )}
          </div>
          {loading && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(auto-fill, minmax(300px, 1fr))' : 'repeat(auto-fill, minmax(340px, 1fr))',
                gap: isMobile ? '16px' : '20px',
              }}
              role="status"
              aria-label={searchQuery ? 'Searching schools' : 'Loading schools'}
              aria-busy="true"
            >
              {Array.from({ length: 6 }).map((_, i) => <SchoolCardSkeleton key={i} />)}
            </div>
          )}
          
          {!loading && schools.length > 0 && (
            <div 
              data-school-grid
              style={{ 
                display: 'grid', 
                gridTemplateColumns: isMobile 
                  ? '1fr' 
                  : isTablet 
                    ? 'repeat(auto-fill, minmax(300px, 1fr))' 
                    : 'repeat(auto-fill, minmax(340px, 1fr))', 
                gap: isMobile ? '16px' : '20px' 
              }}
            >
              {schools.map(school => (
                // Plain wrapper. This was `content-visibility:auto` for cheap
                // virtualization (audit #149), but its paint containment clipped the
                // card's hover lift and broke the top border on hover. The card is
                // React.memo'd and the grid is paginated, so dropping it costs little.
                <div key={school.school_id}>
                  <SchoolCard
                    school={school}
                    onCardClick={handleSchoolClick}
                    onAddToList={handleAddToList}
                    schoolInteractions={schoolInteractions[school.school_id] || { heart: false, star: false, bookmark: false }}
                    isLoading={Object.keys(pillLoadingStates).some(key => key.startsWith(`${school.school_id}-`))}
                    customLists={customLists.map(l => ({ id: l.id, name: l.name, color: l.color, school_count: l.school_count }))}
                    schoolCustomListIds={schoolMemberships[school.school_id] || []}
                    onToggleCustomList={handleToggleCustomList}
                    onCreateCustomList={handleCreateCustomListFromCard}
                    onReportCoach={setReportSchoolId}
                  />
                </div>
              ))}
            </div>
          )}

          {!loading && loadError && schools.length === 0 && (
            <ErrorState
              message="We couldn't load schools just now. Check your connection and try again."
              onRetry={() => loadSchools()}
            />
          )}

          {!loading && !loadError && schools.length === 0 && (
            <EmptyState
              icon={<Search size={24} />}
              title="No schools found"
              subtitle="Try adjusting your filters or search to see more programs."
            />
          )}

          {/* Sentinel for intersection observer to trigger loading more - only when not initial loading */}
          {!loading && <div ref={loaderRef as any} style={{ height: 1, width: '100%' }} />}
          
          {/* Loading more indicator - only show when NOT doing initial load */}
          {!loading && loadingMore && (
            <div style={{ 
              textAlign: 'center', 
              padding: '24px', 
              color: '#64748b',
              fontSize: '14px'
            }}>
              <div style={{
                display: 'inline-block',
                width: '20px',
                height: '20px',
                border: '3px solid #E5E7EB',
                borderTop: '3px solid #F9550D',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                marginRight: '8px'
              }} />
              Loading more schools...
            </div>
          )}
          
          {/* No more schools indicator */}
          {!loading && !loadingMore && !hasMore && schools.length > 0 && (
            <div style={{ 
              textAlign: 'center', 
              padding: '24px', 
              color: '#9CA3AF',
              fontSize: '14px'
            }}>
              You've reached the end of the list
            </div>
          )}
          </div>
        </div>
        
        {/* Filter Sidebar - only show on desktop when on "All Schools" view */}
        {activeView === 'all' && !isMobile && !isTablet && (
          <FilterSidebar 
            onApplyFilters={handleApplyFilters} 
            onClearFilters={handleClearFilters}
            externalFilters={filters}
          />
        )}
      </div>

      {/* Mobile Filter Modal */}
      {(isMobile || isTablet) && isFilterModalOpen && (
        <>
          {/* Backdrop */}
          <div 
            onClick={() => setIsFilterModalOpen(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1000,
            }}
          />
          {/* Slide-up Panel */}
          <div style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            maxHeight: '85vh',
            background: 'white',
            borderRadius: '16px 16px 0 0',
            zIndex: 1001,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}>
            {/* Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px 20px',
              borderBottom: '1px solid #e5e7eb',
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '18px',
                fontWeight: '600',
                color: '#151133',
              }}>
                Filters
              </h3>
              <button
                onClick={() => setIsFilterModalOpen(false)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '36px',
                  height: '36px',
                  background: '#f3f4f6',
                  border: 'none',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  color: '#6b7280',
                }}
              >
                <X size={20} />
              </button>
            </div>
            {/* Filter Content */}
            <div style={{
              flex: 1,
              ...containedScroll,
              padding: '0 0 16px',
            }}>
              <FilterSidebar 
                onApplyFilters={(newFilters) => {
                  handleApplyFilters(newFilters);
                  setIsFilterModalOpen(false);
                }} 
                onClearFilters={() => {
                  handleClearFilters();
                  setIsFilterModalOpen(false);
                }}
                externalFilters={filters}
                isMobileModal={true}
              />
            </div>
          </div>
        </>
      )}

      {/* School Profile Modal */}
      <SchoolProfileModal
        isOpen={isSchoolModalOpen}
        onClose={handleCloseSchoolModal}
        schoolId={selectedSchoolId}
        athleteProfileId={athleteId}
        initialTab={schoolModalTab}
      />

      {reportSchoolId != null && (
        <CoachReportModal
          isOpen
          onClose={() => setReportSchoolId(null)}
          schoolId={reportSchoolId}
          schoolName={schools.find(s => s.school_id === reportSchoolId)?.name}
          athleteId={athleteId || undefined}
          loadCoaches
        />
      )}

      {/* APS-259 step 4 */}
      <WelcomeVideoModal
        isOpen={welcomeOpen}
        onClose={handleWelcomeClose}
        videoUrl={gamePlan?.welcome.video_url}
        posterUrl={gamePlan?.welcome.poster_url}
      />
      {athleteId && addVideoOpen && (
        <AddVideoModal
          isOpen
          onClose={() => setAddVideoOpen(false)}
          athleteId={athleteId}
          isSelf={gamePlan ? gamePlan.athlete.is_self : user?.user_type === 'athlete'}
          athleteFirstName={gamePlan?.athlete.first_name || (athleteSnapshot as any)?.first_name}
          currentUrl={gamePlan?.steps.find((s) => s.key === 'video')?.detail.featured_url || null}
          youtubeConnected={!!gamePlan?.steps.find((s) => s.key === 'video')?.detail.youtube_connected}
          onSaved={() => {
            setAddVideoOpen(false);
            swrInvalidate((k) => k === 'myAthletes:first');
            loadGamePlan();
          }}
          onYoutubeChanged={() => loadGamePlan()}
        />
      )}
      {athleteId && (
        <EmailProgramsModal
          isOpen={emailProgramsOpen}
          onClose={() => setEmailProgramsOpen(false)}
          athleteId={athleteId}
          ctx={{
            isSelf: gamePlan ? gamePlan.athlete.is_self : user?.user_type === 'athlete',
            first: cleanFirstName(gamePlan?.athlete.first_name || (athleteSnapshot as any)?.first_name),
          }}
          gmailConnected={gamePlan?.steps.find((s) => s.key === 'gmail')?.detail.status === 'connected'}
          onPickSchools={showMatches}
          onDone={() => loadGamePlan()}
        />
      )}
    </AppLayout>
  );
};

export default Dashboard;
